import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Anfrage,
  AnfrageStatus,
  ANFRAGE_STATUS_ORDER,
  anfragenApi,
  customersApi,
} from '@/lib/api';
import { Plus } from 'lucide-react';
import { AmRechnerBearbeiten, Button, SegmentedControl, Spinner, formatEuro, toast } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { KanbanColumn } from './components/KanbanColumn';
import { PhasenListe } from './components/PhasenListe';
import { AnfrageFormModal } from './components/AnfrageFormModal';
import { AnfrageCloseModal } from './components/AnfrageCloseModal';

type ViewMode = 'all' | 'open' | 'closed';

const OPEN_STATUSES: AnfrageStatus[] = ['NEU', 'IN_BEARBEITUNG', 'IN_PLANUNG'];
const CLOSED_STATUSES: AnfrageStatus[] = ['GEWONNEN', 'VERLOREN'];

function anfrageValue(anfrage: Anfrage): number {
  if (anfrage.value === null || anfrage.value === undefined) return 0;
  const num =
    typeof anfrage.value === 'string' ? parseFloat(anfrage.value) : anfrage.value;
  return Number.isNaN(num) ? 0 : num;
}

export function PipelinePage() {
  const [anfragen, setAnfragen] = useState<Anfrage[]>([]);
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('open');
  const [draggedAnfrageId, setDraggedAnfrageId] = useState<string | null>(null);

  // Modal states
  const [showAnfrageModal, setShowAnfrageModal] = useState(false);
  const [closingAnfrage, setClosingAnfrage] = useState<Anfrage | null>(null);

  // Aus der Kundenakte kommt man mit ?customer_id=… hierher; der Dialog
  // geht dann direkt mit diesem Kunden auf.
  const [searchParams, setSearchParams] = useSearchParams();
  const vorgewaehlterKunde = searchParams.get('customer_id');

  const loadData = useCallback(async () => {
    // Anfragen und Kundennamen getrennt laden: Bis zum 09.09.2026 hingen
    // beide in einem Promise.all, und die Kundenliste scheiterte immer
    // (per_page=200, die API erlaubt 100). Dadurch zeigte das Board nie eine
    // Anfrage — die Namen sind Beiwerk, sie duerfen die Anfragen nicht
    // mitreissen.
    try {
      setLoading(true);
      setAnfragen(await anfragenApi.list());
    } catch (error) {
      // Kein toast hier: useToast liefert den Context unmemoisiert, und mit
      // toast in den Abhaengigkeiten wuerde jeder Fehler-Toast loadData neu
      // erzeugen, den Effekt erneut ausloesen — und bei anhaltendem Fehler
      // eine Schleife drehen.
      console.error('Failed to load anfragen:', error);
    } finally {
      setLoading(false);
    }

    try {
      const names: Record<string, string> = {};
      for (const c of await customersApi.listAll()) {
        names[c.id] = c.company_name;
      }
      setCustomerNames(names);
    } catch (error) {
      console.error('Failed to load customer names:', error);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (vorgewaehlterKunde) setShowAnfrageModal(true);
  }, [vorgewaehlterKunde]);

  // Which status columns to show for the current view.
  const visibleStatuses = ANFRAGE_STATUS_ORDER.filter((status) => {
    switch (viewMode) {
      case 'open':
        return OPEN_STATUSES.includes(status);
      case 'closed':
        return CLOSED_STATUSES.includes(status);
      default:
        return true;
    }
  });

  // Group anfragen by status.
  const anfragenByStatus = ANFRAGE_STATUS_ORDER.reduce((acc, status) => {
    acc[status] = anfragen.filter((a) => a.status === status);
    return acc;
  }, {} as Record<AnfrageStatus, Anfrage[]>);

  // Totals (across visible columns).
  const visibleAnfragen = anfragen.filter((a) => visibleStatuses.includes(a.status));
  const totalValue = visibleAnfragen.reduce((sum, a) => sum + anfrageValue(a), 0);
  const wonAnfragen = anfragen.filter((a) => a.status === 'GEWONNEN');
  const wonValue = wonAnfragen.reduce((sum, a) => sum + anfrageValue(a), 0);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, status: AnfrageStatus) => {
    e.preventDefault();
    const anfrageId = e.dataTransfer.getData('text/plain') || draggedAnfrageId;
    setDraggedAnfrageId(null);

    if (!anfrageId) return;

    const anfrage = anfragen.find((a) => a.id === anfrageId);
    if (!anfrage || anfrage.status === status) return;

    // GEWONNEN/VERLOREN benötigen ggf. einen Verlustgrund -> über Modal abschließen.
    if (status === 'GEWONNEN' || status === 'VERLOREN') {
      setClosingAnfrage(anfrage);
      return;
    }

    const previousStatus = anfrage.status;
    // Optimistic update
    setAnfragen((prev) =>
      prev.map((a) => (a.id === anfrageId ? { ...a, status } : a)),
    );

    try {
      const antwort = await anfragenApi.transition(anfrageId, status, anfrage.version);
      // Eine gelungene Uebergabe sagte bis zum 28.10.2026 nichts (Ü-12): kein
      // Toast, kein Weg ins Projekt. Jetzt nennt sie die Projektnummer, unter
      // der das Projekt in PM steht.
      if (antwort.uebergabe) {
        const nummer = antwort.uebergabe.project_number;
        toast.success(nummer ? `An PM übergeben: ${nummer}` : 'An PM übergeben');
      }
      // Refresh, damit version + ggf. project_ref_id (IN_PLANUNG-Handoff) aktuell sind.
      await loadData();
      // Der Uebergang ist gelungen, das Projekt steht — aber vielleicht ohne
      // Kartenpunkt. Ohne diese Zeile blieb der Grund in der Antwort stehen
      // und niemand sah ihn; die Luecke fiel erst Tage spaeter in der
      // Kalkulation auf.
      if (antwort?.hinweis) {
        toast.warning(antwort.hinweis);
      }
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { detail?: unknown } };
      };
      // Revert on error
      setAnfragen((prev) =>
        prev.map((a) =>
          a.id === anfrageId ? { ...a, status: previousStatus } : a,
        ),
      );
      if (axiosError.response?.status === 422) {
        toast.error('Dieser Statuswechsel ist nicht erlaubt');
      } else if (axiosError.response?.status === 409) {
        toast.error('Die Anfrage wurde inzwischen geändert. Seite neu laden.');
      } else if (
        axiosError.response?.status === 502 &&
        typeof axiosError.response.data?.detail === 'string'
      ) {
        // Handoff an PM gescheitert: der Grund steht im detail (fehlende
        // Adresse, PM nicht erreichbar). Ohne ihn hiess alles nur
        // "fehlgeschlagen", und niemand konnte etwas tun.
        toast.error(axiosError.response.data.detail);
      } else {
        toast.error('Statuswechsel fehlgeschlagen');
      }
      console.error('Failed to transition anfrage:', error);
    }
  };

  // Modal handlers
  const handleAddAnfrage = () => {
    setShowAnfrageModal(true);
  };

  const handleCloseAnfrage = (anfrage: Anfrage) => {
    setClosingAnfrage(anfrage);
  };

  const handleModalClose = () => {
    setShowAnfrageModal(false);
    if (vorgewaehlterKunde) setSearchParams({}, { replace: true });
  };

  const handleAnfrageSave = async () => {
    await loadData();
    handleModalClose();
  };

  const handleCloseSave = async () => {
    await loadData();
    setClosingAnfrage(null);
  };

  // Add drag listeners to anfrage cards
  useEffect(() => {
    const handleGlobalDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      const card = target.closest('[data-anfrage-id]');
      if (card) {
        const anfrageId = card.getAttribute('data-anfrage-id');
        if (anfrageId) {
          setDraggedAnfrageId(anfrageId);
          e.dataTransfer?.setData('text/plain', anfrageId);
        }
      }
    };

    const handleGlobalDragEnd = () => {
      setDraggedAnfrageId(null);
    };

    document.addEventListener('dragstart', handleGlobalDragStart);
    document.addEventListener('dragend', handleGlobalDragEnd);

    return () => {
      document.removeEventListener('dragstart', handleGlobalDragStart);
      document.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, []);

  const formatCurrency = (value: number) => formatEuro(value);

  if (loading) {
    return (
      <>
        <CrmPageHeader title="Pipeline" />
        <div className="flex min-h-[400px] items-center justify-center">
          <Spinner size={32} />
        </div>
      </>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <CrmPageHeader
        title="Pipeline"
        subtitle={
          <>
            {visibleAnfragen.length} Anfragen · {formatCurrency(totalValue)} Gesamtwert
            {wonValue > 0 && <span className="text-success"> · {formatCurrency(wonValue)} gewonnen</span>}
          </>
        }
        actions={
          // Am Handy nur lesen (M2): Anlegen und Verschieben am Rechner.
          <Button variant="primary" icon={Plus} onClick={handleAddAnfrage} className="max-md:hidden">
            Anfrage anlegen
          </Button>
        }
      >
        <SegmentedControl
          label="Anfragen anzeigen"
          value={viewMode}
          onChange={(v) => setViewMode(v as ViewMode)}
          items={[
            { value: 'open', label: 'Offene Anfragen' },
            { value: 'closed', label: 'Abgeschlossen' },
            { value: 'all', label: 'Alle' },
          ]}
        />
      </CrmPageHeader>

      {/* Am Handy eine Liste je Phase, ohne Ziehen (M2, nur lesen). */}
      <div className="mt-4 space-y-4 md:hidden">
        <AmRechnerBearbeiten>Anfragen anlegen, verschieben und abschließen geht am Rechner.</AmRechnerBearbeiten>
        {visibleStatuses.map((status) => (
          <PhasenListe
            key={status}
            status={status}
            anfragen={anfragenByStatus[status] || []}
            customerNames={customerNames}
          />
        ))}
      </div>

      {/* Kanban Board */}
      <div className="mt-4 flex-1 overflow-x-auto max-md:hidden">
        <div className="flex h-full min-w-max gap-4">
          {visibleStatuses.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              anfragen={anfragenByStatus[status] || []}
              customerNames={customerNames}
              onAnfrageClose={viewMode !== 'closed' ? handleCloseAnfrage : undefined}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      </div>

      {/* Anfrage Form Modal */}
      {showAnfrageModal && (
        <AnfrageFormModal
          initialCustomerId={vorgewaehlterKunde ?? undefined}
          onClose={handleModalClose}
          onSave={handleAnfrageSave}
        />
      )}

      {/* Anfrage Close Modal */}
      {closingAnfrage && (
        <AnfrageCloseModal
          anfrage={closingAnfrage}
          customerName={customerNames[closingAnfrage.customer_id]}
          onClose={() => setClosingAnfrage(null)}
          onSave={handleCloseSave}
        />
      )}
    </div>
  );
}
